import resource from "@core/Util/resources";
import brickTypes from "@modules/JourneyBuilder/Bricks/brick-types";
import Vue from 'vue';
import DedupBaseIcon from "@core/Modules/JourneyBuilder/Assets/Checkpoints/icon_dedup_B.png";
import DedupTargetIcon from "@core/Modules/JourneyBuilder/Assets/Checkpoints/icon_dedup_T.png";
import api from "@modules/JourneyBuilder/api";
import {timeUnit, fromSeconds} from '@core/Util/time';

const timeUnitLabelLookup = Object.keys(timeUnit)
  .reduce((lookup, label) => {
      lookup[timeUnit[label]] = label;
      return lookup;
  }, {});

const ORDER_EXCLUDED = -1337;

/** @returns {Promise<{ get: function(key: String):String }>} */
function getFilterLabelMap(){
    if(getFilterLabelMap._cachedResult) {
        return getFilterLabelMap._cachedResult;
    }

    return getFilterLabelMap._cachedResult = new Promise(async resolve => {
        /** @type {Map<String, String>} */
        const map = new Map();

        try {
            const fieldTypes = await api.getAudienceFilterFieldTypes();

            fieldTypes.forEach(({id, type, label, value}) => {
                map.set(type, label); //Label = type label
                map.set(id, value);   //Value = item label            
            });
        } catch (e) {
            //Should never occur
            getFilterLabelMap._cachedResult = undefined;
        }

        //Worst case - keys as labels :(
        resolve({
            get: key => map.get(key) || key || ''
        });
    });
}


/**
 * Checkpoint tooltip list element builder
 */
class ListElementBuilder {
    constructor() {
        this._rootElement = document.createElement('ul');
    }

    /**
     * Create a nested list appending it at the current position to the current list element
     * @return {ListElementBuilder}
     */
    createdNestedList() {
        const childList = new ListElementBuilder();
        this._rootElement.appendChild(childList._rootElement);
        return childList;
    }

    /**
     * Add a listitem to the current list element
     * @param {String} text
     * @return {ListElementBuilder}
     */
    add(text) {
        const el = document.createElement('li');
        el.innerText = text;
        this._rootElement.appendChild(el);
        return this;
    }

    /**
     * Get the amount of elements contained within the list element
     * @return {number}
     */
    get count() {
        return this._rootElement.childElementCount;
    }

    toString() {
        if(this.count === 0) { 
            return '';
        }
        
        return this._rootElement.outerHTML;
    }
}

/**
 * @type {JourneyBuilder.Checkpoint}
 */
class Checkpoint {
    constructor(
        name, 
        displayNameResource, 
        descriptionResource,
        { iconPath, iconClass }
    ) {
        this.name = name;
        this.iconClass = iconClass;
        this.iconPath = iconPath;
        
        this._displayNameResource = displayNameResource;
        this._descriptionResource = descriptionResource;
    }
    
    get defaultOrder(){
        //Default to final position
        return Number.MAX_SAFE_INTEGER;
    }
    
    get displayName(){
        return resource(this._displayNameResource);
    }

    get description() {
        return resource(this._descriptionResource)
    }
    
    isConfiguredForConnection(connection){
        //Check if the value is not an empty object
        return !!Object.keys(this.readForConnection(connection)).length;
    }

    /**
     * @param {JourneyBrickConnection} connection
     * @return {Object|undefined}
     */
    readForConnection(connection){
        return connection.config[this.name];
    }

    /**
     * @param {JourneyBrickConnection} connection
     * @return {boolean}
     */
    isActiveForConnection(connection){
        return !!connection.config[this.name];
    }

    /**
     * @param {JourneyBrickConnection} connection
     */
    activateForConnection(connection){
        if(!connection.supportedCheckpoints.includes(this.name)) {
            return;
        }
        
        const order = [];
        
        //Ensure isActive returns true for self
        Vue.set(connection.config, this.name, {});

        checkpointDefinitionsArray
            .filter(checkpointDefinition => checkpointDefinition.defaultOrder !== ORDER_EXCLUDED && checkpointDefinition.isActiveForConnection(connection))
            .sort((l, r) => l.defaultOrder > r.defaultOrder ? 1 : 0)
            .forEach(checkpointDefinition => order.push(checkpointDefinition.name));
        
        Vue.set(connection.config, 'order', order);
    }

    /**
     * @param {JourneyBrickConnection} connection
     */
    deactivateForConnection(connection){
        //Vue.delete(connection.config[Checkpoint.CONFIG_PROPERTY], this.name);
        Vue.delete(connection.config, this.name);
        
        if(!connection.config.order){
            return;
        }
        
        //Not all checkpoints are included in order
        const orderIndex = connection.config.order.indexOf(this.name);
        if(orderIndex !== -1){
            connection.config.order.splice(orderIndex, 1);
        }
    }

    /**
     * @param {JourneyBrickConnection} connection
     * @returns {JourneyBuilder.Tooltip}
     */
    buildTooltip(connection){ //<-- rename, buildContentTooltip?
        return {
            title: undefined,
            content: undefined
        }
    }

    /**
     * @return {JourneyBuilder.Tooltip}
     */
    buildInformationTooltip() {
        return {
            title: this.displayName,
            content: this.description
        }
    }
}

class FilterCheckpoint extends Checkpoint {
    constructor() {
        super('filter', 'JourneyBuilder_Checkpoint_Filter', 'JourneyBuilder_Checkpoint_Filter_Description', { iconClass: 'fas fa-map-signs' });
    }

    get defaultOrder() {
        return 0;
    }

    buildTooltip(connection) {
        const config = this.readForConnection(connection);

        const tooltipContent = new Promise(async resolve => {
            if(!config || !config.conditions) {
                return undefined;
            }
            
            const filterLabelMap = await getFilterLabelMap();
            let content = '';

            config.conditions.forEach(group => {
                const builder = new ListElementBuilder();
                group.forEach(
                  condition => builder.add(`${filterLabelMap.get(condition.field)} - ${filterLabelMap.get(condition.value)}`)
                );
                content += builder;
            });

            resolve(content);
        });
        
        return {
            title: 'Filter checkpoint',
            content: tooltipContent
        }
    }
}

class DelayCheckpoint extends Checkpoint {
    constructor() {
        super('offset', 'JourneyBuilder_Checkpoint_Delay', 'JourneyBuilder_Checkpoint_Delay_Description', { iconClass: 'fas fa-clock' });
    }

    get defaultOrder() {
        return 3;
    }
    
    get daysOfTheWeek() {
        return [
            { value: null, label: resource('JourneyBuilder_Moment_Immediate')},
            { value: 0, label: resource('Day_Sunday')},
            { value: 1, label: resource('Day_Monday')},
            { value: 2, label: resource('Day_Tuesday')},
            { value: 3, label: resource('Day_Wednesday')},
            { value: 4, label: resource('Day_Thursday')},
            { value: 5, label: resource('Day_Friday')},
            { value: 6, label: resource('Day_Saturday')}
        ];
    }
    
    buildTooltip(connection) {
        const config = this.readForConnection(connection);
        const contentBuilder = new ListElementBuilder();
        
        if(config.dateField) {
            contentBuilder.add(
              `${resource('JourneyBuilder_FilterSettings_Offset_Column')} - ${config.dateField}`
            );
        }
        
        if(config.seconds) {
            contentBuilder.add(
                `${fromSeconds(config.seconds, config.unit)} ${timeUnitLabelLookup[config.unit]}`
            );
        }
        
        // noinspection EqualityComparisonWithCoercionJS
        contentBuilder.add(this.daysOfTheWeek.find(dow => dow.value == config.weekDay).label);
        
        return {
            title: 'Delay checkpoint',
            content: contentBuilder.toString()
        }
    }
}

class DeduplicationTargetCheckpoint extends Checkpoint {
    constructor() {
        super('dedupTarget', 'JourneyBuilder_Checkpoint_DeduplicationTarget', 'JourneyBuilder_Checkpoint_DeduplicationTarget_Description', { iconPath: DedupTargetIcon });
    }

    get defaultOrder() {
        return 1;
    }
    
    buildTooltip(connection) {
        const config = this.readForConnection(connection);

        const builder = new ListElementBuilder();
        
        if(config.fields) {
            config.fields.forEach(field => builder.add(field));   
        }

        if(config.term && config.term.seconds) {
            builder.add(
              `${resource('JourneyBuilder_DeduplicationSettings_Term_Label')} - ${fromSeconds(config.term.seconds, config.term.unit)} ${timeUnitLabelLookup[config.term.unit]}`
            );
        }

        return {
            title: 'Deduplication checkpoint (target)',
            content: builder.toString()
        }
    }
}

class DeduplicationBaseCheckpoint extends Checkpoint {
    constructor() {
        super('dedupBase', 'JourneyBuilder_Checkpoint_DeduplicationBase', 'JourneyBuilder_Checkpoint_DeduplicationBase_Description', { iconPath: DedupBaseIcon });
    }

    get defaultOrder() {
        return 2;
    }

    buildTooltip(connection) {
        const config = this.readForConnection(connection);
        
        const tooltipContent = new Promise(async resolve => {
            const filterLabelMap = await getFilterLabelMap();
            let content = '';

            if(!config || !config.conditions) {
                return content;
            }
            
            config.conditions.forEach(group => {
                const builder = new ListElementBuilder();
                
                group.forEach(condition => {
                    builder.add(condition.field);
                    const subBuilder = builder.createdNestedList();
                    condition.value.forEach(
                      audienceId => subBuilder.add(filterLabelMap.get(audienceId))
                    );
                });
                
                content += builder;
            });
            
            resolve(content);
        });
        
        return {
            title: 'Deduplication checkpoint (base)',
            content: tooltipContent
        }
    }
}

class FilterExclusionCheckpoint extends Checkpoint {
    constructor() {
        super('filterExclusionCheckpoint', 'JourneyBuilder_Checkpoint_FilterExclusion', 'JourneyBuilder_Checkpoint_FilterExclusion_Description', { iconClass: 'far fa-list-alt' })
    }
    
    get defaultOrder(){
        return ORDER_EXCLUDED;
    }

    isConfiguredForConnection(connection){
        return true; //Does not have an actual configuration
    }
    
    buildTooltip(connection) {
        return {
            title: 'Filter exclusion checkpoint',
        };
    }

    /**
     * Toggle the given filter for exclusion in the given connection configuration
     * @param {Object} config connection configuration
     * @param {String} filterId
     * @param {boolean} isExcluded
     */
    toggleFilterExclusion(config, filterId, isExcluded) {
        if(!config.excludedFilters){
            Vue.set(config, 'excludedFilters', []);
        }
        
        if(isExcluded){
            config.excludedFilters.push(filterId);
        } else {
            const filterIndex = config.excludedFilters.indexOf(filterId);
            
            if(filterIndex !== -1) {
                config.excludedFilters.splice(filterIndex, 1);   
            }
        }
    }
}

class OptionsCheckpoint extends Checkpoint {
    constructor() {
        super('options', 'Options', '', { iconClass: 'fas fa-bars' })
    }

    get defaultOrder(){
        return ORDER_EXCLUDED;
    }

    isConfiguredForConnection(connection){
        return true; //Always configured
    }
    
    isActiveForConnection(connection){
        return true;
    }

    buildTooltip(connection) {
        return {
            title: 'Options',
        }
    }
}

const filterCheckpoint = new FilterCheckpoint();
const delayCheckpoint = new DelayCheckpoint();
const deduplicationTargetCheckpoint = new DeduplicationTargetCheckpoint();
const deduplicationBaseCheckpoint = new DeduplicationBaseCheckpoint();
const optionsCheckpoint = new OptionsCheckpoint();
const filterExclusionCheckpoint = new FilterExclusionCheckpoint();

/**
 * @type {Object.<string, JourneyBuilder.Checkpoint>}
 */
const checkpointDefinitions = {
    [filterCheckpoint.name]: filterCheckpoint,
    [delayCheckpoint.name]: delayCheckpoint,
    [deduplicationTargetCheckpoint.name]: deduplicationTargetCheckpoint,
    [deduplicationBaseCheckpoint.name]: deduplicationBaseCheckpoint,
    [optionsCheckpoint.name]: optionsCheckpoint,
    [filterExclusionCheckpoint.name]: filterExclusionCheckpoint
};

/**
 * @type {JourneyBuilder.Checkpoint[]}
 */
const checkpointDefinitionsArray = [
    deduplicationTargetCheckpoint,
    deduplicationBaseCheckpoint,
    delayCheckpoint,
    filterCheckpoint,
    filterExclusionCheckpoint,
    optionsCheckpoint,
];

export {
    Checkpoint,
    filterCheckpoint,
    delayCheckpoint,
    deduplicationTargetCheckpoint,
    deduplicationBaseCheckpoint,
    optionsCheckpoint,
    filterExclusionCheckpoint,
    
    checkpointDefinitions,
    checkpointDefinitionsArray
}